import React, { useState } from 'react';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { auth, db } from '../../firebase/config';
import { doc, getDoc, collection, query, where, getDocs, setDoc, deleteDoc } from 'firebase/firestore';
import './Auth.css';

const SignupScreen = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState('email'); // 'email' or 'password'
  const [approvedData, setApprovedData] = useState(null);

  const checkEmailApproved = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      console.log('Checking email approval for:', email.toLowerCase());
      
      // Check if email exists in users collection (pre-approved)
      console.log('Querying users collection...');
      const usersQuery = query(collection(db, 'users'), where('email', '==', email.toLowerCase()));
      const userSnapshot = await getDocs(usersQuery);
      console.log('Users query completed, found:', userSnapshot.size, 'documents');
      
      if (!userSnapshot.empty) {
        const userData = userSnapshot.docs[0].data();
        console.log('Found pre-approved user data:', userData);
        setApprovedData({ doc: userSnapshot.docs[0], data: userData });
        setStep('password');
        return;
      }

      // Check if user is in waitlist with activated status
      console.log('Querying waitlist collection...');
      const waitlistQuery = query(
        collection(db, 'waitlist'), 
        where('email', '==', email.toLowerCase()),
        where('status', '==', 'activated')
      );
      const waitlistSnapshot = await getDocs(waitlistQuery);
      console.log('Waitlist query completed, found:', waitlistSnapshot.size, 'documents');
      
      if (!waitlistSnapshot.empty) {
        console.log('Found activated waitlist entry');
        setApprovedData({ waitlist: true });
        setStep('password');
        return;
      }

      console.log('No approved email found');
      setError('This email is not approved for signup. Please join our waitlist first.');
      
    } catch (error) {
      console.error('Error checking email approval:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      setError('Error checking email status. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Create Firebase Auth account
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // If there's pre-approved data, migrate it to the new UID
      if (approvedData?.doc) {
        const preApprovedData = approvedData.data;
        
        // Create user document with the new UID but keep existing data
        await setDoc(doc(db, 'users', user.uid), {
          ...preApprovedData,
          email: user.email,
          updatedAt: new Date(),
          firebaseAuthUID: user.uid
        }, { merge: true });
        
        // Delete the old pre-approved document
        await deleteDoc(doc(db, 'users', approvedData.doc.id));
        
        console.log('Pre-approved user account created and migrated:', user.email);
      } else {
        // Create new user document for waitlist-approved users
        await setDoc(doc(db, 'users', user.uid), {
          email: user.email,
          isSubscribed: true,
          subscriptionType: 'free',
          subscriptionStartDate: new Date(),
          maxAccounts: 1,
          maxVideosPerMonth: 10,
          videosUsedThisMonth: 0,
          currentPeriodStart: new Date(),
          twitterUsernames: [],
          activatedFromWaitlist: true,
          createdAt: new Date(),
          updatedAt: new Date(),
          isUnlimited: false,
          totalVideos: 0,
          recentImports: []
        }, { merge: true });
        
        console.log('New approved user account created:', user.email);
      }

    } catch (error) {
      console.error('Error creating account:', error);
      if (error.code === 'auth/email-already-in-use') {
        setError('An account with this email already exists. Please sign in instead.');
      } else if (error.code === 'auth/weak-password') {
        setError('Password is too weak. Please choose a stronger password.');
      } else {
        setError('Account creation failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    setStep('email');
    setPassword('');
    setConfirmPassword('');
    setError('');
    setApprovedData(null);
  };

  return (
    <div className="clerk-auth-container">
      <div className="clerk-auth-wrapper">
        <div className="clerk-auth-card">
          <div className="clerk-auth-header">
            <div className="clerk-logo">
              <img src="/logo.png" alt="Logo" className="clerk-logo-image" />
            </div>
            <div className="clerk-auth-title">
              <h1>Create your account</h1>
              <p className="clerk-auth-description">
                {step === 'email' 
                  ? 'Enter your email to check if you\'re approved for early access'
                  : 'Set your password to complete account creation'
                }
              </p>
            </div>
          </div>

          <div className="clerk-auth-content">
            {step === 'email' ? (
              <form onSubmit={checkEmailApproved} className="clerk-form">
                <div className="clerk-field">
                  <label className="clerk-label" htmlFor="email">
                    Email address
                  </label>
                  <input
                    id="email"
                    type="email"
                    placeholder="Enter your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="clerk-input"
                    disabled={loading}
                  />
                </div>

                {error && (
                  <div className="clerk-error">
                    {error}
                  </div>
                )}

                <button 
                  type="submit" 
                  className="clerk-submit-btn"
                  disabled={loading || !email}
                >
                  {loading ? (
                    <div className="clerk-spinner"></div>
                  ) : (
                    'Check Email'
                  )}
                </button>
              </form>
            ) : (
              <form onSubmit={handleSignup} className="clerk-form">
                <div className="clerk-field">
                  <label className="clerk-label">
                    Email address
                  </label>
                  <div className="clerk-input clerk-input-readonly">
                    {email}
                  </div>
                </div>

                <div className="clerk-field">
                  <label className="clerk-label" htmlFor="password">
                    Password
                  </label>
                  <input
                    id="password"
                    type="password"
                    placeholder="Create a password (min 6 characters)"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="clerk-input"
                    disabled={loading}
                    minLength={6}
                  />
                </div>

                <div className="clerk-field">
                  <label className="clerk-label" htmlFor="confirmPassword">
                    Confirm Password
                  </label>
                  <input
                    id="confirmPassword"
                    type="password"
                    placeholder="Confirm your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="clerk-input"
                    disabled={loading}
                    minLength={6}
                  />
                </div>

                {error && (
                  <div className="clerk-error">
                    {error}
                  </div>
                )}

                <button 
                  type="submit" 
                  className="clerk-submit-btn"
                  disabled={loading || !password || !confirmPassword}
                >
                  {loading ? (
                    <div className="clerk-spinner"></div>
                  ) : (
                    'Create Account'
                  )}
                </button>

                <button 
                  type="button" 
                  className="clerk-link-btn"
                  onClick={goBack}
                  disabled={loading}
                >
                  ← Back to email
                </button>
              </form>
            )}

            <div className="clerk-footer">
              <button 
                className="clerk-link-btn"
                onClick={() => window.history.pushState(null, '', '/')}
                type="button"
                disabled={loading}
              >
                Already have an account? Sign in
              </button>
            </div>
          </div>

          <div className="clerk-secured">
            <span className="clerk-secured-text">Secured by</span>
            <div className="clerk-secured-logo">
              <span className="clerk-secured-icon">⚡</span>
              <span>Twizz</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupScreen; 